import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _to_property_key } from "@swc/helpers/_/_to_property_key";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import NavigationBuilderContext from "./NavigationBuilderContext";
import NavigationContext from "./NavigationContext";
import NavigationRouteContext from "./NavigationRouteContext";
import SceneView from "./SceneView";
import useNavigationCache from "./useNavigationCache";
import useRouteCache from "./useRouteCache";
/**
 * Hook to create descriptor objects for the child routes.
 *
 * A descriptor object provides 3 things:
 * - Helper method to render a screen
 * - Options specified by the screen for the navigator
 * - Navigation object intended for the route
 */ export default function useDescriptors(param) {
    var state = param.state, screens = param.screens, navigation = param.navigation, screenOptions = param.screenOptions, defaultScreenOptions = param.defaultScreenOptions, onAction = param.onAction, getState = param.getState, setState = param.setState, addListener = param.addListener, addKeyedListener = param.addKeyedListener, onRouteFocus = param.onRouteFocus, router = param.router, emitter = param.emitter;
    var _React_useState = _sliced_to_array(React.useState({}), 2), options = _React_useState[0], setOptions = _React_useState[1];
    var _React_useContext = React.useContext(NavigationBuilderContext), onDispatchAction = _React_useContext.onDispatchAction, onOptionsChange = _React_useContext.onOptionsChange, stackRef = _React_useContext.stackRef;
    var context = React.useMemo(function() {
        return {
            navigation: navigation,
            onAction: onAction,
            addListener: addListener,
            addKeyedListener: addKeyedListener,
            onRouteFocus: onRouteFocus,
            onDispatchAction: onDispatchAction,
            onOptionsChange: onOptionsChange,
            stackRef: stackRef
        };
    }, [
        navigation,
        onAction,
        addListener,
        addKeyedListener,
        onRouteFocus,
        onDispatchAction,
        onOptionsChange,
        stackRef
    ]);
    var navigations = useNavigationCache({
        state: state,
        getState: getState,
        navigation: navigation,
        setOptions: setOptions,
        router: router,
        emitter: emitter
    });
    var routes = useRouteCache(state.routes);
    return routes.reduce(function(acc, route, i) {
        var config = screens[route.name];
        var screen = config.props;
        var navigation = navigations[route.key];
        var optionsList = [
            // The default `screenOptions` passed to the navigator
            screenOptions
        ].concat(// The `screenOptions` props passed to `Group` elements
        _to_consumable_array(config.options ? config.options.filter(Boolean) : []), [
            // The `options` prop passed to `Screen` elements,
            screen.options,
            // The options set via `navigation.setOptions`
            options[route.key]
        ]);
        var customOptions = optionsList.reduce(function(acc, curr) {
            return Object.assign(acc, // @ts-expect-error: we check for function but TS still complains
            typeof curr !== "function" ? curr : curr({
                route: route,
                navigation: navigation
            }));
        }, {});
        var mergedOptions = _object_spread({}, typeof defaultScreenOptions === "function" ? defaultScreenOptions({
            route: route,
            navigation: navigation,
            options: customOptions
        }) : defaultScreenOptions, customOptions);
        var clearOptions = function() {
            return setOptions(function(o) {
                if (route.key in o) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    var _route_key = route.key, _ = o[_route_key], rest = _object_without_properties(o, [
                        _route_key
                    ].map(_to_property_key));
                    return rest;
                }
                return o;
            });
        };
        acc[route.key] = {
            route: route,
            // @ts-expect-error: it's missing action helpers, fix later
            navigation: navigation,
            render: function render() {
                return /*#__PURE__*/ _jsx(NavigationBuilderContext.Provider, {
                    value: context,
                    children: /*#__PURE__*/ _jsx(NavigationContext.Provider, {
                        value: navigation,
                        children: /*#__PURE__*/ _jsx(NavigationRouteContext.Provider, {
                            value: route,
                            children: /*#__PURE__*/ _jsx(SceneView, {
                                navigation: navigation,
                                route: route,
                                screen: screen,
                                routeState: state.routes[i].state,
                                getState: getState,
                                setState: setState,
                                options: mergedOptions,
                                clearOptions: clearOptions
                            })
                        })
                    })
                }, route.key);
            },
            options: mergedOptions
        };
        return acc;
    }, {});
}
